import { FC, useState } from 'react';
import { Snackbar, Alert, AlertTitle, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useSnackbar } from '@/hooks/useSnackbar';

/**
 * Global Snackbar component for displaying notifications
 * Uses MUI Snackbar and Alert components
 */
const GlobalSnackbar: FC = () => {
  const { open, message, severity, autoHideDuration, hideSnackbar, title, supportInfo } = useSnackbar();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    hideSnackbar();
  };

  return (
    <>
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key={message}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          sx={{ width: '100%' }}
          role="alert"
          aria-live="assertive"
          action={
            supportInfo ? (
              <>
                <Button
                  color="inherit"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailsOpen(true);
                  }}
                >
                  Details
                </Button>
                <Button color="inherit" size="small" onClick={handleClose}>
                  Dismiss
                </Button>
              </>
            ) : undefined
          }
        >
          {title ? <AlertTitle>{title}</AlertTitle> : null}
          {/* pre-line preserves newlines from multi-line plain-text messages (e.g. ProblemDetails.detail). */}
          <span style={{ whiteSpace: 'pre-line' }}>{message}</span>
        </Alert>
      </Snackbar>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Error details</DialogTitle>
        <DialogContent dividers>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            {supportInfo ? JSON.stringify(supportInfo, null, 2) : 'No additional details available.'}
          </pre>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(
                  supportInfo ? JSON.stringify(supportInfo, null, 2) : 'No additional details available.'
                );
              } catch {
                // ignore clipboard errors
              }
            }}
            color="primary"
          >
            Copy
          </Button>
          <Button onClick={() => setDetailsOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GlobalSnackbar;
